import type { Vec3 } from '../../geo/types';
import type { MeshBuilder, VertexStyle } from '../mesh/meshBuilder';
import type { ShipTemplate, SailState } from './shipTemplates';
import { hullStations, bowTopHeight, type HullStation } from './hull';
import { sparSolid, clothMembrane } from './shipSolids';
import { sailclothStyle } from './shipPatterns';
import {
  RIG_SPAN,
  ROPE_RADIUS,
  MAST,
  YARD_PAINTER_WIDTH_SCALE,
  bowPoint,
  buildMast,
  buildShrouds,
  mastAt,
  mastRadiusAt,
  rope,
  stationAtX,
  type Mast,
  type RigStyles,
} from './rigParts';

/*
 * The painting's small craft under sail (berths R27, R28 and R35, drawn in profile) set a sprit
 * mainsail abaft the mast and a foresail from high on the mast down to the stem; bare, they keep
 * the sprit standing (R11-R13). On the scan the throat sits at the masthead and the peak stands
 * 1.10-1.31 mast heights above the deck, 0.35-0.49 of one abaft the mast. The throat here keeps
 * just under the forestay's lashing at 0.97 of the mast.
 */
const MAINSAIL = {
  throat: 0.92,
  peak: 1.1,
  peakAft: 0.38,
  lift: 0.4,
  columns: 5,
  rows: 6,
  bulge: 0.05,
  clearance: 0.05,
} as const;
const SPRIT = { heel: 0.12, radius: 0.1, taper: 0.7, sides: 6, clearance: 0.03 } as const;
const FORESAIL = {
  stay: 0.97,
  lift: 0.4,
  columns: 3,
  rows: 4,
  bulge: 0.05,
  clearance: 0.03,
} as const;
const BRAIL = {
  foot: 0.35,
  head: 0.9,
  footRadius: 0.15,
  headRadius: 0.3,
  clearance: 0.03,
} as const;
/* A pennant hangs 1.3 m below the top of its staff (pennant.ts), so a staff reaching 1.5 m above
   the peak flies it clear of the sail and the sprit whatever the wind. */
const STAFF_OVER_PEAK = 1.5;

/**
 * The painting draws its sprit sails nearly flat in profile: sheeted 10-20 degrees to the lee, a
 * mainsail shows the painter camera, abeam of it, at least cos 20 degrees of its face.
 */
export const SHEET = { min: (10 * Math.PI) / 180, max: (20 * Math.PI) / 180 } as const;
/* A sheet stepped to 20 degrees may come out a rounding error past SHEET.max; any further, the
   sails would reach the lee shrouds and leave the room the rig keeps. */
const SHEET_TOLERANCE = 1e-9;

/* A point in a mainsail's own frame: `aft` of the mast's axis along the sail, `y` up, and `lee`
   off the sail's plane towards the side it is sheeted to. */
interface SailPoint {
  readonly aft: number;
  readonly y: number;
  readonly lee: number;
}

interface Quadrilateral {
  readonly tack: SailPoint;
  readonly throat: SailPoint;
  readonly peak: SailPoint;
  readonly clew: SailPoint;
}

/* The foresail in the centre plane before it is sheeted, and the stay it is bent to. */
interface Foresail {
  readonly head: Vec3;
  readonly tack: Vec3;
  readonly clew: Vec3;
  readonly stay: readonly [Vec3, Vec3];
}

/* A mainsail's plane swung `sheet` about the mast: its aft and lee directions in plan. */
interface SheetedPlane {
  readonly mastX: number;
  readonly aft: readonly [number, number];
  readonly lee: readonly [number, number];
}

function sheetedPlane(mast: Mast, sheet: number): SheetedPlane {
  const side = sheet < 0 ? -1 : 1;
  return {
    mastX: mast.x,
    aft: [Math.cos(sheet), Math.sin(sheet)],
    lee: [-side * Math.sin(sheet), side * Math.cos(sheet)],
  };
}

function placed(plane: SheetedPlane, p: SailPoint): Vec3 {
  return [
    plane.mastX + p.aft * plane.aft[0] + p.lee * plane.lee[0],
    p.y,
    p.aft * plane.aft[1] + p.lee * plane.lee[1],
  ];
}

function between(a: SailPoint, b: SailPoint, t: number): SailPoint {
  return {
    aft: a.aft + (b.aft - a.aft) * t,
    y: a.y + (b.y - a.y) * t,
    lee: a.lee + (b.lee - a.lee) * t,
  };
}

function mix(a: Vec3, b: Vec3, t: number): Vec3 {
  return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];
}

/* The foot clears the gunwale by the lift wherever it passes over the hull. */
function footHeight(
  stations: readonly HullStation[],
  mast: Mast,
  clewAft: number,
  length: number
): number {
  const over = stations.map((s) => s.x - mast.x).filter((aft) => aft > 0 && aft < clewAft);
  const below = [0, ...over, clewAft].map((aft) => stationAtX(stations, mast.x + aft, length));
  return Math.max(...below.map((s) => s.gunwale)) + MAINSAIL.lift;
}

/*
 * The clew lies as far abaft the mast as keeps it within 0.6 beams of the centre line sheeted
 * out to SHEET.max, the room every rig keeps: 7.0 m on the boeier, 0.54 of its mast's height,
 * where the painting's clews lie 0.44-0.58 mast heights abaft the mast.
 */
function mainsailOf(
  template: ShipTemplate,
  stations: readonly HullStation[],
  mast: Mast
): Quadrilateral {
  const clewAft = (RIG_SPAN * template.beam) / 2 / Math.sin(SHEET.max);
  const foot = footHeight(stations, mast, clewAft, template.length);
  const throat = mast.deck + MAINSAIL.throat * mast.height;
  const luff = (y: number): number => mastRadiusAt(mast, y) + MAINSAIL.clearance;
  return {
    tack: { aft: luff(foot), y: foot, lee: 0 },
    throat: { aft: luff(throat), y: throat, lee: 0 },
    peak: {
      aft: MAINSAIL.peakAft * mast.height,
      y: mast.deck + MAINSAIL.peak * mast.height,
      lee: 0,
    },
    clew: { aft: clewAft, y: foot, lee: 0 },
  };
}

/* Cloth between the head (throat to peak) and the foot (tack to clew), bellied to the lee. */
function buildMainsail(
  builder: MeshBuilder,
  style: VertexStyle,
  sail: Quadrilateral,
  plane: SheetedPlane
): void {
  const chord = Math.hypot(sail.clew.aft - sail.tack.aft, sail.clew.y - sail.tack.y);
  const grid = Array.from({ length: MAINSAIL.rows + 1 }, (_, r) =>
    Array.from({ length: MAINSAIL.columns + 1 }, (_, c): Vec3 => {
      const [u, v] = [c / MAINSAIL.columns, r / MAINSAIL.rows];
      const p = between(between(sail.throat, sail.peak, u), between(sail.tack, sail.clew, u), v);
      const fill = Math.sin(Math.PI * u) * Math.sin(Math.PI * v);
      return placed(plane, { ...p, lee: MAINSAIL.bulge * chord * fill });
    })
  );
  clothMembrane(builder, style, grid);
}

/* The sprit is lashed low on the mast and lies along the windward face of the sail to its peak. */
function buildSprit(
  builder: MeshBuilder,
  style: VertexStyle,
  mast: Mast,
  peak: SailPoint,
  plane: SheetedPlane
): void {
  const heel = mast.deck + SPRIT.heel * mast.height;
  const tipRadius = SPRIT.radius * SPRIT.taper;
  sparSolid(builder, style, {
    from: placed(plane, {
      aft: mastRadiusAt(mast, heel) + SPRIT.radius,
      y: heel,
      lee: -(SPRIT.radius + SPRIT.clearance),
    }),
    to: placed(plane, { aft: peak.aft, y: peak.y, lee: -(tipRadius + SPRIT.clearance) }),
    radiusFrom: SPRIT.radius,
    radiusTo: tipRadius,
    sides: SPRIT.sides,
    painterWidthScale: YARD_PAINTER_WIDTH_SCALE,
  });
}

/*
 * The luff runs beside the stay, clear of the rope, from where it clears the mast down to the
 * lift above the stem head; the clew lies just before the mast, the lift above its gunwale.
 */
function foresailOf(
  template: ShipTemplate,
  stations: readonly HullStation[],
  mast: Mast
): Foresail {
  const top: Vec3 = [mast.x, mast.deck + FORESAIL.stay * mast.height, 0];
  const foot = bowPoint(template, stations);
  const [dx, dy] = [foot[0] - top[0], foot[1] - top[1]];
  const offset = (ROPE_RADIUS + FORESAIL.clearance) / Math.hypot(dx, dy);
  const luff = (t: number): Vec3 => [
    top[0] + t * dx - dy * offset,
    top[1] + t * dy + dx * offset,
    0,
  ];
  const beforeMast = (t: number): number =>
    mast.x - luff(t)[0] - mastRadiusAt(mast, luff(t)[1]) - FORESAIL.clearance;
  const [near, far] = [beforeMast(0), beforeMast(1)];
  const tackY = bowTopHeight(stations) + FORESAIL.lift;
  const clewX = mast.x - (MAST.radius + FORESAIL.clearance);
  const clewY = stationAtX(stations, clewX, template.length).gunwale + FORESAIL.lift;
  return {
    head: luff(-near / (far - near)),
    tack: luff((tackY - luff(0)[1]) / dy),
    clew: [clewX, clewY, 0],
    stay: [top, foot],
  };
}

function difference(a: Vec3, b: Vec3): Vec3 {
  return [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
}

/* A turn by `angle` about the line through `origin` along the unit `axis` (Rodrigues). */
function turnAbout(origin: Vec3, axis: Vec3, angle: number): (p: Vec3) => Vec3 {
  const [cos, sin] = [Math.cos(angle), Math.sin(angle)];
  return (p) => {
    const v = difference(p, origin);
    const along = (axis[0] * v[0] + axis[1] * v[1] + axis[2] * v[2]) * (1 - cos);
    return [
      origin[0] + v[0] * cos + (axis[1] * v[2] - axis[2] * v[1]) * sin + axis[0] * along,
      origin[1] + v[1] * cos + (axis[2] * v[0] - axis[0] * v[2]) * sin + axis[1] * along,
      origin[2] + v[2] * cos + (axis[0] * v[1] - axis[1] * v[0]) * sin + axis[2] * along,
    ];
  };
}

/* Cloth from the head, where its rows meet, down to the foot; sheeted by a turn about the luff. */
function buildForesail(
  builder: MeshBuilder,
  style: VertexStyle,
  sail: Foresail,
  sheet: number
): void {
  const side = sheet < 0 ? -1 : 1;
  const luff = difference(sail.tack, sail.head);
  const reach = Math.hypot(...luff);
  const turn = turnAbout(sail.head, [luff[0] / reach, luff[1] / reach, luff[2] / reach], sheet);
  const chord = Math.hypot(...difference(sail.clew, sail.tack));
  const grid = Array.from({ length: FORESAIL.rows + 1 }, (_, r) =>
    Array.from({ length: FORESAIL.columns + 1 }, (_, c): Vec3 => {
      const [u, v] = [c / FORESAIL.columns, r / FORESAIL.rows];
      const p = mix(mix(sail.head, sail.tack, v), mix(sail.head, sail.clew, v), u);
      const fill = Math.sin(Math.PI * u) * Math.sin(Math.PI * v);
      return turn([p[0], p[1], p[2] + side * FORESAIL.bulge * chord * fill]);
    })
  );
  clothMembrane(builder, style, grid);
}

/* Brailed, the mainsail is gathered up against the aft side of the mast under the throat. */
function buildBrailedSail(builder: MeshBuilder, style: VertexStyle, mast: Mast): void {
  const at = (height: number, radius: number): Vec3 => {
    const y = mast.deck + height * mast.height;
    return [mast.x + mastRadiusAt(mast, y) + radius + BRAIL.clearance, y, 0];
  };
  sparSolid(builder, style, {
    from: at(BRAIL.foot, BRAIL.footRadius),
    to: at(BRAIL.head, BRAIL.headRadius),
    radiusFrom: BRAIL.footRadius,
    radiusTo: BRAIL.headRadius,
    sides: MAST.sides,
    painterWidthScale: 1,
  });
}

/** How far the pennant staff of a sprit-rigged mast stands above its head. */
export function spritStaffHeight(mast: Mast): number {
  return (MAINSAIL.peak - 1) * mast.height + STAFF_OVER_PEAK;
}

/**
 * A sprit rig (bow at -X): the mast with its shrouds and a forestay to the stem, and the sprit
 * from low on the mast to the peak of the mainsail. Set, the mainsail and the foresail swing
 * `sheet` to the side its sign gives; furled, the mainsail is brailed against the mast.
 */
export function buildSpritRig(
  builder: MeshBuilder,
  styles: RigStyles,
  template: ShipTemplate,
  sailState: SailState,
  sheet: number
): void {
  if (template.masts.length !== 1) {
    throw new Error(`A sprit rig has one mast, got ${template.masts.length}`);
  }
  if (Math.abs(sheet) > SHEET.max + SHEET_TOLERANCE) {
    throw new Error(`A sprit rig's sails sheet out at most ${SHEET.max} rad, got ${sheet}`);
  }
  const stations = hullStations(template);
  const mast = mastAt(stations, template.masts[0]!);
  const plane = sheetedPlane(mast, sheet);
  const mainsail = mainsailOf(template, stations, mast);
  const foresail = foresailOf(template, stations, mast);
  buildMast(builder, styles.spar, mast, spritStaffHeight(mast));
  buildSprit(builder, styles.spar, mast, mainsail.peak, plane);
  buildShrouds(builder, styles.rope, mast, stations, template.length);
  rope(builder, styles.rope, ...foresail.stay);
  if (sailState === 'furled') {
    buildBrailedSail(builder, styles.sail, mast);
    return;
  }
  const cloth = sailclothStyle(styles.sail, 0);
  buildMainsail(builder, cloth, mainsail, plane);
  buildForesail(builder, cloth, foresail, sheet);
}
